import { supabase, isSupabaseConfigured, resolveFunctionError } from '@/lib/supabase';
import type { Permissions, PermissionLevel } from '@/lib/rbac';
import { recordAudit } from '@/services/audit';

export interface RoleRecord {
  id: string;
  name: string;
  description: string;
  permissions: Permissions;
}

// Nombre visible del rol. La tabla `roles` guarda el slug canónico en `name`
// (p. ej. "sales_agent") y el nombre amigable en `description` (p. ej.
// "Agente Comercial / Ventas"). Para la UI siempre se prioriza `description`.
export function roleLabel(role: RoleRecord): string {
  return role.description?.trim() || role.name;
}

export interface UserRecord {
  id: string;
  email: string;
  fullName: string;
  roleId: string | null;
  roleName: string;
  isActive: boolean;
  createdAt: string;
}

// Módulos canónicos del panel (coinciden con ROUTE_MODULE_MAP de rbac.ts).
export const PERMISSION_MODULES: Array<{ key: string; label: string }> = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'orders', label: 'Pedidos' },
  { key: 'produccion', label: 'Producción' },
  { key: 'sales', label: 'Ventas' },
  { key: 'customers', label: 'Clientes' },
  { key: 'products', label: 'Productos' },
  { key: 'sizes', label: 'Tallas' },
  { key: 'inventory', label: 'Inventario' },
  { key: 'inventory_movements', label: 'Movimientos' },
  { key: 'purchases', label: 'Compras' },
  { key: 'suppliers', label: 'Proveedores' },
  { key: 'distributors', label: 'Distribuidores' },
  { key: 'consignments', label: 'Consignaciones' },
  { key: 'salespersons', label: 'Vendedores' },
  { key: 'commissions', label: 'Comisiones' },
  { key: 'payments', label: 'Pagos' },
  { key: 'expenses', label: 'Gastos' },
  { key: 'donations', label: 'Donaciones' },
  { key: 'returns', label: 'Devoluciones' },
  { key: 'exchanges', label: 'Cambios' },
  { key: 'warranties', label: 'Garantías' },
  { key: 'campaigns', label: 'Marketing' },
  { key: 'reports', label: 'Reportes' },
  { key: 'documents', label: 'Documentos' },
  { key: 'users', label: 'Usuarios' },
  { key: 'audit', label: 'Auditoría' },
  { key: 'settings', label: 'Configuración' },
  { key: 'faqs', label: 'Preguntas Frecuentes' },
  { key: 'discounts', label: 'Descuentos' },
];

export const EMPTY_PERMISSION: PermissionLevel = { read: false, write: false, delete: false };

export function emptyPermissions(): Permissions {
  const out: Permissions = {};
  PERMISSION_MODULES.forEach((m) => {
    out[m.key] = { ...EMPTY_PERMISSION };
  });
  return out;
}

// --- Roles ---
export async function fetchRoles(): Promise<RoleRecord[]> {
  if (!isSupabaseConfigured || !supabase) return [];
  const { data, error } = await supabase
    .from('roles')
    .select('id, name, description, permissions')
    .order('name');
  if (error) {
    console.warn('[users] fetchRoles error', error.message);
    return [];
  }
  return ((data ?? []) as Array<Record<string, any>>).map((r) => ({
    id: r.id,
    name: r.name,
    description: r.description ?? '',
    permissions: (r.permissions ?? {}) as Permissions,
  }));
}

export async function createRole(name: string, description: string): Promise<void> {
  if (!supabase) throw new Error('Supabase no está configurado');
  const { data, error } = await supabase.from('roles').insert({
    name: name.trim(),
    description: description.trim(),
    permissions: emptyPermissions(),
  }).select('id');
  if (error) throw new Error(error.message);
  const roleId = (data?.[0] as { id?: string } | undefined)?.id;
  if (roleId) {
    recordAudit({
      entityType: 'roles',
      entityId: roleId,
      action: 'CREATE',
      newValue: name.trim(),
    });
  }
}

export async function updateRolePermissions(id: string, permissions: Permissions): Promise<void> {
  if (!supabase) throw new Error('Supabase no está configurado');
  const { error } = await supabase.from('roles').update({ permissions }).eq('id', id);
  if (error) throw new Error(error.message);
  recordAudit({
    entityType: 'roles',
    entityId: id,
    action: 'UPDATE',
    fieldName: 'permissions',
    newValue: 'Permisos actualizados',
  });
}

// --- Usuarios ---
export async function fetchUsers(roles: RoleRecord[]): Promise<UserRecord[]> {
  if (!isSupabaseConfigured || !supabase) return [];
  const { data, error } = await supabase
    .from('app_users')
    .select('id, email, full_name, role_id, is_active, created_at')
    .order('created_at', { ascending: false });
  if (error) {
    console.warn('[users] fetchUsers error', error.message);
    return [];
  }
  const roleMap = new Map(roles.map((r) => [r.id, r.name]));
  return ((data ?? []) as Array<Record<string, any>>).map((u) => ({
    id: u.id,
    email: u.email,
    fullName: u.full_name ?? '',
    roleId: u.role_id ?? null,
    roleName: (u.role_id && roleMap.get(u.role_id)) || 'Sin rol',
    isActive: u.is_active !== false,
    createdAt: u.created_at ?? '',
  }));
}

export async function createUser(input: {
  email: string;
  fullName: string;
  roleId: string;
  password?: string;
}): Promise<{ id: string; tempPassword: string; passwordSet: boolean }> {
  if (!supabase) throw new Error('Supabase no está configurado');
  const { data, error } = await supabase.functions.invoke('manage-users', {
    body: {
      action: 'create',
      email: input.email.trim(),
      fullName: input.fullName.trim(),
      roleId: input.roleId,
      password: input.password?.trim() ?? '',
    },
  });
  if (error) throw new Error(await resolveFunctionError(error));
  if (data?.error) throw new Error(data.error);
  recordAudit({
    entityType: 'users',
    entityId: data?.id ?? '',
    action: 'CREATE',
    newValue: input.fullName.trim(),
  });
  return {
    id: data?.id ?? '',
    tempPassword: data?.tempPassword ?? '',
    passwordSet: data?.passwordSet === true,
  };
}

// Cambia la contraseña de OTRO usuario. Solo disponible para administradores
// (la Edge Function lo verifica). Para cambiar la propia contraseña usa
// `changeMyPassword`.
export async function resetUserPassword(userId: string, newPassword: string): Promise<void> {
  if (!supabase) throw new Error('Supabase no está configurado');
  const { data, error } = await supabase.functions.invoke('manage-users', {
    body: {
      action: 'reset_password',
      userId,
      newPassword,
    },
  });
  if (error) throw new Error(await resolveFunctionError(error));
  if (data?.error) throw new Error(data.error);
  recordAudit({
    entityType: 'users',
    entityId: userId,
    action: 'UPDATE',
    fieldName: 'password',
    newValue: 'Contraseña restablecida',
  });
}

// Cambia la contraseña del usuario actualmente autenticado. No requiere ser
// admin: cualquier usuario con sesión activa puede cambiar su propia clave.
export async function changeMyPassword(newPassword: string): Promise<void> {
  if (!supabase) throw new Error('Supabase no está configurado');
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw new Error(error.message);
}

export async function updateUser(
  id: string,
  patch: { fullName?: string; roleId?: string | null; isActive?: boolean },
): Promise<void> {
  if (!supabase) throw new Error('Supabase no está configurado');
  const snake: Record<string, any> = {};
  if (patch.fullName !== undefined) snake.full_name = patch.fullName;
  if (patch.roleId !== undefined) snake.role_id = patch.roleId;
  if (patch.isActive !== undefined) snake.is_active = patch.isActive;
  const { error } = await supabase.from('app_users').update(snake).eq('id', id);
  if (error) throw new Error(error.message);
  recordAudit({
    entityType: 'users',
    entityId: id,
    action: 'UPDATE',
    newValue:
      patch.fullName ??
      (patch.isActive !== undefined ? `is_active: ${patch.isActive}` : 'Rol actualizado'),
  });
}

// Elimina un usuario del panel (solo administradores; la Edge Function lo
// verifica). Borra la ficha en app_users y la cuenta en Supabase Auth.
export async function deleteUser(id: string): Promise<void> {
  if (!supabase) throw new Error('Supabase no está configurado');
  const { data, error } = await supabase.functions.invoke('manage-users', {
    body: { action: 'delete', userId: id },
  });
  if (error) throw new Error(await resolveFunctionError(error));
  if (data?.error) throw new Error(data.error);
  recordAudit({
    entityType: 'users',
    entityId: id,
    action: 'DELETE',
  });
}